import { ProductCategory } from '@collinsonx/utils/generatedTypes/graphql';
import { useFastTrack } from 'config/features';
import {
  DEFAULT_CONTEXT_PRODUCT_VALUE,
  isAlwaysAvailableSupplier,
} from 'config/productDefaultConfig';
import { ProductContext } from 'context/ProductContext';
import dayjs from 'dayjs';
import usePayload from 'hooks/usePayload';
import { useContext } from 'react';
import { replaceTranslationPlaceholder } from 'utils/translations';

import useBooking from './useBooking';
import useLocale from './useLocale';

const useProduct = () => {
  const { isLocationProduct } = usePayload();
  const { product } = useContext(ProductContext);
  const translation = useLocale();
  const { booking } = useBooking();
  const { departureTime } = booking;

  const nowTimeLocal = dayjs();

  const departureTimeLocal = departureTime;

  const isFastTrackAlwaysAvailable =
    useFastTrack &&
    isLocationProduct &&
    (Boolean(booking?.alwaysAvailable) ||
      (isAlwaysAvailableSupplier(product?.partnerID) &&
        product?.category === ProductCategory.FastTrack));

  const isFastTrackEnabled = useFastTrack && isLocationProduct;

  const canPrebookInMinPeriod =
    product &&
    departureTimeLocal &&
    nowTimeLocal.isBefore(
      dayjs(departureTimeLocal).subtract(
        product.bookingMinPeriodAllowed,
        'hour'
      )
    );

  const canPrebookingInMaxPeriod =
    departureTimeLocal &&
    product &&
    dayjs(departureTimeLocal).isBefore(
      nowTimeLocal.add(product.bookingMaxPeriodAllowed, 'hour')
    );

  const validPrebookPeriod = canPrebookInMinPeriod && canPrebookingInMaxPeriod;
  const checkValidCancellationPeriod = (arrivalTime?: string) => {
    if (!arrivalTime) return translation.booking.cancellation.errors.failed;

    if (dayjs(arrivalTime).isBefore(new Date()))
      return translation.booking.cancellation.errors.expiredBooking;

    if (
      product &&
      nowTimeLocal.isAfter(
        dayjs(arrivalTime).subtract(product.cancellationPeriod, 'hour')
      )
    )
      return replaceTranslationPlaceholder(
        translation.booking.cancellation.errors.notAllowed,
        { time: product.cancellationPeriod }
      ) as string;
  };

  return {
    ...DEFAULT_CONTEXT_PRODUCT_VALUE,
    ...product,
    checkValidCancellationPeriod,
    isFastTrackAlwaysAvailable,
    isFastTrackEnabled,
    isLocationProduct,
    loadingProduct: !product,
    pdfGuideUrl: DEFAULT_CONTEXT_PRODUCT_VALUE.pdfGuideUrl,
    validPrebookPeriod,
  };
};

export default useProduct;
